
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Define TypeScript types for the function parameters and return values
export function usePageSpeedScan() {
  const [result, setResult] = useState<any>(null);
  const [results, setResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const scan = async (url: string, strategy: 'mobile' | 'desktop' = 'mobile') => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke('pagespeed', {
        body: { url, strategy },
      });

      if (error) throw new Error(error.message);
      
      // Add URL to the result for reference
      const resultWithUrl = { ...data, url };
      setResult(resultWithUrl);
      
      // Add to the results array for consistency
      setResults([resultWithUrl]);
      
      return resultWithUrl;
    } catch (err) {
      console.error('PageSpeed scan error:', err);
      setError(err instanceof Error ? err : new Error('Failed to scan URL'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const scanMultiple = async (urls: string[], strategy: 'mobile' | 'desktop' = 'mobile') => {
    setIsLoading(true);
    setError(null);
    setResults([]);

    try {
      const { data, error } = await supabase.functions.invoke('pagespeed', {
        body: { urls, strategy },
      });

      if (error) throw new Error(error.message);
      
      // If we receive an array of results
      if (Array.isArray(data)) {
        setResults(data);
        // If there's only one result, also set it as the single result for backward compatibility
        if (data.length === 1) {
          setResult(data[0]);
        }
      } else {
        // If we receive a single result, wrap it in an array
        const resultWithUrl = { ...data, url: urls[0] };
        setResults([resultWithUrl]);
        setResult(resultWithUrl);
      }
      
      return results;
    } catch (err) {
      console.error('PageSpeed scan error:', err);
      setError(err instanceof Error ? err : new Error('Failed to scan URLs'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    scan,
    scanMultiple,
    result,
    results,
    isLoading,
    error,
  };
}
